/**
 * useMissionCreatePermission
 *
 * The server's answer whether the signed-in member may create missions
 * (permissionsApi.mayCreateMissions). Creation hints and the designer link
 * show only on `true`; `undefined` means the answer is being read or could
 * not be read, and the caller shows neither the action nor a refusal.
 *
 * One read per member per page load: the answer is kept per user id.
 */

import { useEffect, useState } from 'react'
import { useAuth } from '@agion/auth'
import { permissionsApi } from '../services/permissions-api'

const answers = new Map<string, Promise<boolean>>()

function answerFor(userId: string): Promise<boolean> {
  let answer = answers.get(userId)
  if (!answer) {
    answer = permissionsApi.mayCreateMissions()
    // A failed read is not kept: the next mount asks again.
    answer.catch(() => answers.delete(userId))
    answers.set(userId, answer)
  }
  return answer
}

export function useMissionCreatePermission(): boolean | undefined {
  const { user } = useAuth()
  const userId = user?.id
  const [answer, setAnswer] = useState<{ userId: string; mayCreate: boolean } | null>(null)

  useEffect(() => {
    if (!userId) return
    let current = true
    answerFor(userId)
      .then((mayCreate) => {
        if (current) setAnswer({ userId, mayCreate })
      })
      .catch(() => {
        if (current) setAnswer(null)
      })
    return () => {
      current = false
    }
  }, [userId])

  // An answer read for another member is never shown.
  return answer && answer.userId === userId ? answer.mayCreate : undefined
}

/** Test seam: forget every kept answer. */
export function resetMissionCreatePermissionForTests(): void {
  answers.clear()
}
